import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { invPositionService } from "../services/svc_invposition";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest } from "../types/common/commonTypes";
import { InvPosRequest } from "../types/invPosTypes";

export const useTSQInvPositionList = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invposition.list(params),
    queryFn: () => invPositionService.list(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQInvPositionListLot = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invposition.list(params),
    queryFn: () => invPositionService.listByLot(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQInvPositionListItem = (
  params: ListRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invposition.list(params),
    queryFn: () => invPositionService.listByItem(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQInvPositionFulfillment = (
  itemId: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invposition.itemList(itemId),
    queryFn: () => invPositionService.listForFulfillment(itemId),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQInvPositionListv2 = (
  request: InvPosRequest,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.invposition.listv2(request),
    queryFn: () => invPositionService.listv2(request),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQInvPosition = (id: string) => {
  return useQuery({
    queryKey: queryKeys.invposition.detail(id),
    queryFn: () => {
      return invPositionService.getById(id);
    },
    enabled: !!id,
  });
};

export const useTSMCreateInvPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => {
      return invPositionService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invposition.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to create Position:", error);
    },
  });
};

export const useTSMUpdateInvPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => {
      return invPositionService.update(id, data);
    },
    onSuccess: (updatedPosition) => {
      queryClient.setQueryData(
        queryKeys.invposition.detail(updatedPosition.id),
        updatedPosition,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invposition.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to update Position:", error);
    },
  });
};

export const useTSMDeactivateInvPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invPositionService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invposition.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to deactivate Position:", error);
    },
  });
};

export const useTSMReactivateInvPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: invPositionService.reactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.invposition.lists(),
      });
    },
    onError: (error) => {
      console.error("Failed to reactivate Position:", error);
    },
  });
};
